import React, { useState, useEffect } from "react";
import "@/App.css";
import { Navigation } from "./components/Navigation";
import { Hero } from "./components/Hero";
import { Philosophy } from "./components/Philosophy";
import { TheBook } from "./components/TheBook";
import { PrivateClasses } from "./components/PrivateClasses";
import { Impressum } from "./components/Impressum";
import { Datenschutz } from "./components/Datenschutz";
import { translations } from "./constants/translations";
import { LANDING } from "./constants/testIds/landing";
import { ArrowUp, Shield, Youtube, Instagram } from "lucide-react";

function App() {
  const [lang, setLang] = useState("de"); // Default to Austrian/German
  const [impressumOpen, setImpressumOpen] = useState(false);
  const [datenschutzOpen, setDatenschutzOpen] = useState(false);
  const [showScrollTop, setShowScrollTop] = useState(false);

  // Active translation
  const t = translations[lang];

  useEffect(() => {
    const handleScroll = () => {
      setShowScrollTop(window.scrollY > 400);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <div className="min-h-screen bg-[#efe7dd] text-[#1c110e] font-sans selection:bg-[#8a2424] selection:text-[#f7f2ed] overflow-x-hidden relative">
      {/* Premium crystal-glass navigation */}
      <Navigation
        lang={lang}
        setLang={setLang}
        t={t}
        onOpenImpressum={() => setImpressumOpen(true)}
        onOpenDatenschutz={() => setDatenschutzOpen(true)}
      />

      {/* Hero Section */}
      <Hero t={t} />

      {/* Philosophy Section */}
      <Philosophy t={t} />

      {/* Book Section */}
      <TheBook t={t} />

      {/* Private Classes Section */}
      <PrivateClasses t={t} />

      {/* Social Media Section */}
      <section className="bg-[#e5d8cb] border-t border-[#b58e70]/30 py-20 sm:py-28">
        <div className="max-w-3xl mx-auto px-6 sm:px-8 text-center space-y-10">
          <h2 className="font-serif text-3xl sm:text-4xl text-[#1c110e] font-light tracking-tight">
            {t.social.title}
          </h2>
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-center gap-4">
            <a
              href="https://www.youtube.com/@walterunterberger8643"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="social-youtube-link"
              className="group flex items-center justify-center gap-3 bg-[#8a2424] text-[#f7f2ed] hover:bg-[#a32e2e] transition-colors duration-300 px-8 py-4 border border-[#8a2424] hover:border-[#a32e2e]"
            >
              <Youtube className="w-5 h-5" />
              <span className="font-sans text-xs sm:text-sm tracking-[0.15em] uppercase font-semibold">
                {t.social.youtube}
              </span>
            </a>
            <a
              href="https://www.instagram.com/tango_wien/"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="social-instagram-link"
              className="group flex items-center justify-center gap-3 border border-[#8a2424] text-[#8a2424] hover:bg-[#8a2424] hover:text-[#f7f2ed] transition-all duration-300 px-8 py-4"
            >
              <Instagram className="w-5 h-5" />
              <span className="font-sans text-xs sm:text-sm tracking-[0.15em] uppercase font-semibold">
                {t.social.instagram}
              </span>
            </a>
          </div>
        </div>
      </section>

      {/* Premium Footer */}
      <footer className="bg-[#e5d8cb] border-t border-[#b58e70]/30 py-16 sm:py-24 relative overflow-hidden">
        {/* Absolute subtle glowing shape */}
        <div className="absolute right-0 bottom-0 w-80 h-80 rounded-full bg-[#8a2424]/5 blur-[80px] pointer-events-none" />

        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 relative z-10">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-12 items-start">
            
            {/* Column 1: Brand details */}
            <div className="md:col-span-6 space-y-6">
              <div className="flex flex-col">
                <span className="font-serif text-2xl tracking-[0.15em] text-[#1c110e] uppercase">
                  Regina & Walter
                </span>
                <span className="font-sans text-xs tracking-[0.25em] text-[#b58e70] uppercase">
                  {t.footer.tagline}
                </span>
              </div>
              <p className="font-sans text-sm text-[#5a4b46] leading-relaxed max-w-md font-light">
                {lang === "en" 
                  ? "Since 2021, we teach Argentine Tango with a profound focus on silent communication, natural musical interpretation, and technical freedom."
                  : "Seit 2021 unterrichten wir Tangounterricht mit einem tiefen Fokus auf lautlose Kommunikation, natürliche Musikinterpretation und technische Freiheit."}
              </p>
            </div>

            {/* Column 2: Quick Links */}
            <div className="md:col-span-3 space-y-4">
              <h4 className="font-serif text-base text-[#1c110e] font-medium uppercase tracking-wider text-[#d97036]">
                {lang === "en" ? "Explore" : "Erkunden"}
              </h4>
              <ul className="space-y-3 font-sans text-xs sm:text-sm text-[#5a4b46] font-light">
                <li>
                  <a href="#home" className="hover:text-[#d97036] transition-colors">{t.nav.home}</a>
                </li>
                <li>
                  <a href="#philosophy" className="hover:text-[#d97036] transition-colors">{t.nav.philosophy}</a>
                </li>
                <li>
                  <a href="#book" className="hover:text-[#d97036] transition-colors">{t.nav.book}</a>
                </li>
                <li>
                  <a href="#contact" className="hover:text-[#d97036] transition-colors">{t.nav.contact}</a>
                </li>
              </ul>
            </div>

            {/* Column 3: Legal & Privacy Info */}
            <div className="md:col-span-3 space-y-4">
              <h4 className="font-serif text-base text-[#1c110e] font-medium uppercase tracking-wider text-[#d97036]">
                {lang === "en" ? "Legal & Privacy" : "Rechtliches"}
              </h4>
              <ul className="space-y-3 font-sans text-xs sm:text-sm text-[#5a4b46] font-light">
                <li>
                  <button
                    onClick={() => setImpressumOpen(true)}
                    data-testid={LANDING.impressumLink}
                    className="hover:text-[#d97036] text-left transition-colors"
                  >
                    {t.nav.impressum}
                  </button>
                </li>
                <li>
                  <button
                    onClick={() => setDatenschutzOpen(true)}
                    data-testid={LANDING.datenschutzLink}
                    className="hover:text-[#d97036] text-left transition-colors"
                  >
                    {t.nav.datenschutz}
                  </button>
                </li>
                <li className="flex items-start gap-2 pt-2 border-t border-[#b58e70]/20 text-[#b58e70]">
                  <Shield className="w-4 h-4 shrink-0 mt-0.5" />
                  <span className="text-xs font-sans leading-relaxed">
                    {t.footer.noCookies}
                  </span>
                </li>
              </ul>
            </div>

          </div>

          {/* Copyright line */}
          <div className="mt-16 pt-8 border-t border-[#b58e70]/20 flex flex-col sm:flex-row items-center justify-between text-xs text-[#5a4b46]/70 font-sans font-light gap-4">
            <p>
              &copy; {new Date().getFullYear()} Regina & Walter. {t.footer.allRights}
            </p>
            <p>
              {lang === "en" ? "Hosted by easyname.com" : "Gehostet von easyname.com"}
            </p>
          </div>

        </div>
      </footer>

      {/* Impressum Modal */}
      <Impressum
        isOpen={impressumOpen}
        onClose={() => setImpressumOpen(false)}
      />

      {/* Datenschutz Modal */}
      <Datenschutz
        isOpen={datenschutzOpen}
        onClose={() => setDatenschutzOpen(false)}
      />

      {/* Elegant Scroll to Top Button */}
      {showScrollTop && (
        <button
          onClick={scrollToTop}
          aria-label="Scroll to top"
          className="fixed bottom-8 right-8 z-40 bg-[#8a2424] text-[#f7f2ed] hover:bg-[#a32e2e] hover:text-[#f7f2ed] transition-colors p-3.5 border border-[#8a2424] hover:border-[#a32e2e] shadow-xl animate-fadeIn"
        >
          <ArrowUp className="w-4 h-4" />
        </button>
      )}
    </div>
  );
}

export default App;
